/**
 * Rules and checklist steps (#223).
 *
 * A rule is one provision of a statute, a rule of court or the fee schedule, independent of any action: its citation,
 * what it says in plain language, and optionally where it applies, over the facts of the case every action shares
 * (CASE_FACTS). The rules are kept per document in rules/<document id>.json, and scripts/build-content.py merges them
 * into src/content/rules.json: { "CCP 92(b)": { text, applies, document } }.
 *
 * A step is one item of one action's checklist, in src/actions/<id>.steps.json:
 *   id        stable within the action; the checklist ticks it under this key
 *   text      what to do, as the checklist shows it; "{due}", "{fee.amount}", "{court.name}" fill in values
 *   rules     the citations of the rules it rests on; the checklist shows them as one citation, "CCP 431.30(b)(1), (d)"
 *   localCite optional, a value added to the citation, "{fee.localCite}"
 *   note      optional, shown under it; noteWhen, a condition, shows it only then
 *   phase     prepare, fees, file, serve, deadlines or after
 *   when      optional condition over the wizard's answers and the action's facts (see test() below)
 *   forms     forms.json ids it links
 *   deadline  optional { name, type, from, days | years, nextCourtDay, how }: a date its text (and the action) reads
 *             as {name}; see DEADLINES
 *   court     optional { detail, verify }: a court detail (courtDetail() in lib/courtlayer.js) that replaces the text
 *             where the court has one; elsewhere the text (if any) stands and a "verify" item names the court
 * A step applies when its `when` holds and each rule it cites applies to the case.
 *
 * An action's module exports `rulebook`:
 *   steps     its steps (the .steps.json file)
 *   facts     { name: condition }: conditions its steps share, "limited": "cls in (limitedOver10k, limitedUpTo10k)"
 *   sections  [{ id, title, phases }]: the checklist's sections in order, each holding the steps of its phases in
 *             the order the steps file lists them. Titles are numbered "Phase 1." on, skipping empty sections
 *   vars      (answers, court, dates) => values the steps' text reads, beside {court.name} and the deadlines
 *   verify    [{ text, when }]: the "verify" items, beside the court details' ones
 *   flags, extras  (c, answers, court, dates) => anything else: the flags, then rows (checklist() in lib/civil.js)
 * and `caseFacts.to(answers)` (src/cases.js), which gives the case facts the rules' conditions read.
 */
import RULES from "../content/rules.json" with { type: "json" };
import { checklist, lastDayAfter, lastDayAfterYears, lastDayBefore, BACKWARD_NOTE } from "./civil.js";
import { courtDetail } from "./courtlayer.js";
import { fmt, parse, todayISO } from "./courtdays.js";

export const PHASES = ["prepare", "fees", "file", "serve", "deadlines", "after"];
export const PHASE_TXT = { prepare: "Prepare", fees: "Fees", file: "File", serve: "Serve", deadlines: "Deadlines", after: "After filing" };
// The facts of a case a rule's condition can read: what src/cases.js keeps once per case.
export const CASE_FACTS = ["cls", "limited", "collections", "ud", "kind"];
export { RULES };

/**
 * Citations as the checklist shows them: ["CCP 431.30(b)(1)", "CCP 431.30(d)", "CRC 3.110(d)"] is
 * "CCP 431.30(b)(1), (d); CRC 3.110(d)".
 */
export function formatCites(cites) {
  let out = "", code = null, number = null;
  cites.forEach((c) => {
    const m = c.match(/^(Fee Schedule item|[A-Z]+) (\S+?)((?:\([0-9a-z]+\))*)$/);
    if (!m) { out += (out ? "; " : "") + c; code = number = null; return; }
    if (m[1] === code && m[2] === number && m[3]) out += `, ${m[3]}`;
    else if (m[1] === code) out += `; ${m[2]}${m[3]}`;
    else out += `${out ? "; " : ""}${c}`;
    [code, number] = [m[1], m[2]];
  });
  return out;
}

/* ------------------------------------------------------------ conditions */
/*
 * A condition reads like "verified = yes and not limited":
 *   name = value, name != value, name in (value, value)   an answer's value
 *   name                                                   a fact, or an answer that is "yes"
 *   not x, x and y, x or y, (x)                            in that order of precedence
 * A name is one of the action's facts (its own condition) or a question's key.
 */
const TOKEN = /\s*(\(|\)|,|!=|=|[A-Za-z0-9_.-]+)/y;
const parsed = new Map();

export function parseCondition(src) {
  if (parsed.has(src)) return parsed.get(src);
  const toks = [];
  TOKEN.lastIndex = 0;
  let m;
  while (TOKEN.lastIndex < src.length && (m = TOKEN.exec(src))) toks.push(m[1]);
  if (TOKEN.lastIndex < src.trimEnd().length) throw new Error(`Can't read condition "${src}" at ${TOKEN.lastIndex}`);
  let i = 0;
  const peek = () => toks[i];
  const take = (t) => { if (t && toks[i] !== t) throw new Error(`Condition "${src}": expected "${t}", got "${toks[i]}"`); return toks[i++]; };
  const word = () => { const w = take(); if (!w || /^[(),=!]/.test(w) || ["and", "or", "not", "in"].includes(w)) throw new Error(`Condition "${src}": expected a name, got "${w}"`); return w; };
  const or = () => { const xs = [and()]; while (peek() === "or") { take(); xs.push(and()); } return xs.length > 1 ? { op: "or", xs } : xs[0]; };
  const and = () => { const xs = [not()]; while (peek() === "and") { take(); xs.push(not()); } return xs.length > 1 ? { op: "and", xs } : xs[0]; };
  const not = () => (peek() === "not" ? (take(), { op: "not", x: not() }) : atom());
  const atom = () => {
    if (peek() === "(") { take("("); const x = or(); take(")"); return x; }
    const name = word();
    if (peek() === "=" || peek() === "!=") return { op: take(), name, value: word() };
    if (peek() === "in") {
      take("in"); take("(");
      const values = [word()];
      while (peek() === ",") { take(","); values.push(word()); }
      take(")");
      return { op: "in", name, values };
    }
    return { op: "name", name };
  };
  const ast = or();
  if (i < toks.length) throw new Error(`Condition "${src}": unexpected "${toks[i]}"`);
  parsed.set(src, ast);
  return ast;
}

/** The names a condition reads. */
export function conditionNames(src) {
  const out = new Set();
  const walk = (x) => { if (x.xs) x.xs.forEach(walk); else if (x.x) walk(x.x); else out.add(x.name); };
  walk(parseCondition(src));
  return out;
}

/** Whether condition `src` holds for answers `a`, with the action's `facts`. An empty condition holds. */
export function test(src, a, facts = {}) {
  if (!src) return true;
  const ev = (x) => {
    switch (x.op) {
      case "or": return x.xs.some(ev);
      case "and": return x.xs.every(ev);
      case "not": return !ev(x.x);
      case "=": return a[x.name] === x.value;
      case "!=": return a[x.name] !== x.value;
      case "in": return x.values.includes(a[x.name]);
      default: return x.name in facts ? test(facts[x.name], a, facts) : a[x.name] === "yes" || a[x.name] === true;
    }
  };
  return ev(parseCondition(src));
}

/** A field's `show`, or an option list's filter, from a condition. The function keeps it as `when`, for the checks. */
export function when(src, facts) {
  parseCondition(src);
  const f = (a) => test(src, a, facts);
  f.when = src;
  return f;
}

/** A question's options, each shown when its `when` holds: [{ v, l, when }]. */
export function optsWhen(opts, facts) {
  const f = (a) => opts.filter((o) => test(o.when, a, facts)).map(({ when: _, ...o }) => o);
  f.all = opts;
  return f;
}

/* ------------------------------------------------------------ deadlines */
/**
 * A deadline's date, counted on the court's calendar from answer `from` (today when it's blank):
 *   daysAfter        `days` calendar days after (CCP 12); with nextCourtDay, a last day that isn't a court day moves
 *                    to the next one (CCP 12a)
 *   courtDaysAfter   `days` court days after
 *   daysBefore       `days` calendar days before, counted backward (CCP 12c); a day that isn't a court day becomes
 *                    the court day before it, with a verify item
 *   courtDaysBefore  `days` court days before
 *   yearsAfter       the same date `years` later; nextCourtDay as for daysAfter
 *   date             the date the answer gives
 * Returns { date, raw, moved, how }.
 */
export const DEADLINES = ["daysAfter", "courtDaysAfter", "daysBefore", "courtDaysBefore", "yearsAfter", "date"];

export function deadlineDate(d, a, court) {
  const from = a[d.from] || todayISO();
  const cal = court.calendar;
  let date, raw;
  switch (d.type) {
    case "daysAfter": {
      const x = lastDayAfter(court, from, d.days);
      raw = x.raw; date = d.nextCourtDay ? x.date : x.raw;
      break;
    }
    case "yearsAfter": {
      const x = lastDayAfterYears(court, from, d.years);
      raw = x.raw; date = d.nextCourtDay ? x.date : x.raw;
      break;
    }
    case "courtDaysAfter": raw = date = cal.addCourtDays(parse(from), d.days); break;
    case "courtDaysBefore": raw = date = cal.addCourtDays(parse(from), -d.days); break;
    case "daysBefore": { const x = lastDayBefore(court, from, d.days); raw = x.date; date = x.safe; break; }
    case "date": raw = date = parse(from); break;
    default: throw new Error(`Unknown deadline type ${d.type}`);
  }
  return { date, raw, moved: date.getTime() !== raw.getTime(), how: d.how || "" };
}

/* ------------------------------------------------------------ text */
/** `s` with each "{path}" filled in from `vars`. A Date prints as fmt() does. */
export function fill(s, vars) {
  return (s || "").replace(/\{([A-Za-z0-9_.]+)\}/g, (_, path) => {
    const v = path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), vars);
    if (v === undefined) throw new Error(`No value for {${path}} in "${s}"`);
    return v instanceof Date ? fmt(v) : String(v);
  });
}

/** The placeholders a text reads. */
export const placeholders = (s) => [...(s || "").matchAll(/\{([A-Za-z0-9_.]+)\}/g)].map((m) => m[1]);

/* ------------------------------------------------------------ build */
/** Whether rule `cite` applies to case facts `f`. */
export const ruleApplies = (cite, f) => test(RULES[cite] && RULES[cite].applies, f);

/** The steps of action module `mod` that apply to answers `a`: their `when` holds and each rule they cite applies. */
export function applicable(mod, a) {
  const book = mod.rulebook;
  const f = mod.caseFacts ? mod.caseFacts.to(a) : {};
  return book.steps.filter((s) => test(s.when, a, book.facts) && s.rules.every((c) => ruleApplies(c, f)));
}

/**
 * The checklist of action module `mod` for answers `a` in `court`: the steps that apply, in the rulebook's sections,
 * with dates, fees and the court's details filled in. Returns what checklist().result() does; each item's key is its
 * step's id.
 */
export function buildChecklist(mod, a, court) {
  const book = mod.rulebook;
  const steps = applicable(mod, a);
  const dates = {};
  steps.filter((s) => s.deadline).forEach((s) => {
    dates[s.deadline.name] = { ...deadlineDate(s.deadline, a, court), cite: formatCites(s.rules), step: s.id };
  });
  const values = { court: { name: court.name }, ...(book.vars ? book.vars(a, court, dates) : {}) };
  Object.entries(dates).forEach(([k, d]) => {
    values[k] = { toString: () => fmt(d.date), how: d.how, date: d.date };
  });
  const c = checklist();
  if (book.flags) book.flags(c, a, court, dates);
  book.sections.forEach((sec) => {
    const out = c.section(sec.id, sec.title);
    steps.filter((s) => sec.phases.includes(s.phase)).forEach((s) => {
      const d = s.deadline && dates[s.deadline.name];
      let note = s.note && test(s.noteWhen, a, book.facts) ? fill(s.note, values) : "";
      if (d && d.moved && s.deadline.nextCourtDay) note = `The last day, ${fmt(d.raw)}, isn't a court day, so the period runs to the next one (CCP 12a).`;
      else if (d && d.moved) c.verify(BACKWARD_NOTE);
      if (s.court) {
        const cd = courtDetail(court, s.court.detail, s.court.verify);
        if (cd.known) { c.item(out, s.id, cd.text, cd.cite, "", cd.forms); return; }
        c.verify(cd.verify);
        if (!s.text) return;
      }
      c.item(out, s.id, fill(s.text, values), formatCites(s.rules) + fill(s.localCite, values), note, s.forms);
    });
  });
  if (book.extras) book.extras(c, a, court, dates);
  (book.verify || []).forEach((v) => { if (test(v.when, a, book.facts)) c.verify(fill(v.text, values)); });
  const res = c.result();
  let n = 1;
  res.sections.forEach((s) => { s.title = `Phase ${n++}. ${s.title}`; });
  // An item's key is its step's id, not "<section>.<id>": a step keeps its ticks wherever it's listed.
  res.sections.forEach((s) => s.items.forEach((i) => { i.key = i.key.slice(s.id.length + 1); }));
  return res;
}

/* ------------------------------------------------------------ answers */
/**
 * Every set of answers a wizard's `steps` accept, for the checks: each radio or select field shown takes each of its
 * options in turn, and each date field each of `dates[key]` (or the one date given by `dates.default`). `court` is
 * a court context, for steps that depend on the court.
 */
export function answerSpace(steps, court, dates = {}) {
  const fields = (typeof steps === "function" ? steps(court) : steps).flatMap((s) => s.fields || []);
  const out = [];
  const walk = (i, a) => {
    if (i === fields.length) { out.push({ ...a }); return; }
    const f = fields[i];
    if (f.show && !f.show(a, court)) { walk(i + 1, a); return; }
    let values;
    if (f.type === "date") values = dates[f.k] || [dates.default || "2026-03-02"];
    else if (f.type === "radio" || f.type === "select") values = (typeof f.opts === "function" ? f.opts(a, court) : f.opts).map((o) => o.v);
    else throw new Error(`answerSpace: field ${f.k} is a ${f.type}`);
    values.forEach((v) => walk(i + 1, { ...a, [f.k]: v }));
  };
  walk(0, {});
  return out;
}
